import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import formatDate, {
  formatDateToLongString,
} from "../../../../utils/FormatDate";
import { IStreamDetails } from "../../../../types/streams";
import VenueDetailsDialog from "../../../bookings/view-booking/VenueDialog";
import { VenueData } from "../../../venues/ViewVenue";

interface EventFieldsProps {
  stream: IStreamDetails;
}

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Grid size={{ xs: 4 }}>
    <Typography variant="body2" sx={{ color: "#888" }}>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="medium">
      {value || "N/A"}
    </Typography>
  </Grid>
);

const EventFields: React.FC<EventFieldsProps> = ({ stream }) => {
  const bookingData = stream.booking;
  const serviceType = bookingData?.service;

  const event = bookingData?.events?.[0];
  const service = bookingData?.services?.[0];

  const [hoverVenue, setHoverVenue] = useState<Partial<VenueData> | null>(null);
  const [isVenueDialogOpen, setIsVenueDialogOpen] = useState(false);

  const handleVenueHover = (venue: Partial<VenueData> | undefined) => {
    if (!venue) return;
    setHoverVenue(venue);
    setIsVenueDialogOpen(true);
  };

  const handleVenueDialogClose = () => {
    setIsVenueDialogOpen(false);
  };

  // Helper to render Venue Name field with hover
  const renderVenueName = (venue?: Partial<VenueData>) => (
    <DetailField
      label="Venue Name"
      value={
        <span
          style={{
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: "bold",
          }}
          onMouseEnter={() => handleVenueHover(venue)}
          onClick={() => handleVenueHover(venue)}
        >
          {venue?.name || "N/A"}
        </span>
      }
    />
  );

  // Main switch for different service types
  const renderFields = () => {
    switch (serviceType) {
      case "Funeral":
      case "Funeral_Home":
        return (
          <>
            <DetailField
              label="Deceased Name"
              value={`${bookingData?.deceased?.[0]?.first_name ?? ""} ${
                bookingData?.deceased?.[0]?.surname ?? ""
              }`}
            />
            <DetailField
              label="Other Name"
              value={bookingData?.deceased?.[0]?.second_name ?? ""}
            />
            <DetailField
              label="Nick name"
              value={bookingData?.deceased?.[0]?.nickname ?? ""}
            />
            <DetailField label="Start Time" value={stream.start || ""} />
            <DetailField label="End Time" value={stream.end || ""} />
            <DetailField label="Date" value={formatDate(stream.date)} />
            {renderVenueName(stream.service?.venue)}
            <DetailField label="Address One" value={stream.address_one || ""} />
            <DetailField label="Address Two" value={stream.address_two || ""} />
            <DetailField label="Suburb" value={stream.suburb || ""} />
            <DetailField label="City" value={stream.city || ""} />
            <DetailField
              label="Priest"
              value={stream.service?.preacher_name || ""}
            />
            <DetailField
              label="Eulogist"
              value={stream.service?.eulogist_name || ""}
            />
            <DetailField label="MC" value={stream.service?.mc_name || ""} />
          </>
        );

      case "Church":
        const churchService = bookingData?.services?.[0];
        return (
          <>
            <DetailField
              label="Name of Church"
              value={churchService?.church_name || "N/A"}
            />
            <DetailField label="Title" value={bookingData?.title || "N/A"} />
            <DetailField
              label="Leader Preacher Name"
              value={churchService?.leader_preacher_name || "N/A"}
            />
            <DetailField
              label="Leader Preacher Surname"
              value={churchService?.leader_preacher_surname || "N/A"}
            />
            <DetailField
              label="Preacher Name"
              value={churchService?.preacher_name || "N/A"}
            />
            <DetailField
              label="Preacher Surname"
              value={churchService?.preacher_surname || "N/A"}
            />
            <DetailField
              label="Date of Service"
              value={formatDate(churchService?.date_of_service) || "N/A"}
            />
            <DetailField
              label="Start Time"
              value={churchService?.start_time || "N/A"}
            />
            <DetailField
              label="End Time"
              value={churchService?.end_time || "N/A"}
            />
            <DetailField
              label="Church Phone"
              value={bookingData?.phone || "N/A"}
            />
            <DetailField
              label="Church Email"
              value={bookingData?.email || "N/A"}
            />
            {renderVenueName(churchService?.venue)}
            <DetailField
              label="Address One"
              value={churchService?.venue?.address_one || "N/A"}
            />
            <DetailField
              label="Address Two"
              value={churchService?.venue?.address_two || "N/A"}
            />
            <DetailField
              label="Suburb"
              value={churchService?.venue?.suburb || "N/A"}
            />
            <DetailField
              label="City"
              value={churchService?.venue?.city || "N/A"}
            />
          </>
        );

      case "Conference":
        return (
          <>
            <DetailField label="Title" value={bookingData?.title || "N/A"} />
            <DetailField
              label="Event Start Date"
              value={
                formatDateToLongString(event?.start_date_of_event) || "N/A"
              }
            />
            <DetailField
              label="Event End Date"
              value={formatDateToLongString(event?.end_date_of_event) || "N/A"}
            />
            <DetailField
              label="Start Time"
              value={event?.start_time || "N/A"}
            />
            <DetailField label="End Time" value={event?.end_time || "N/A"} />
            {renderVenueName(event?.venue)}
            <DetailField
              label="Address One"
              value={event?.venue?.address_one || "N/A"}
            />
            <DetailField
              label="Address Two"
              value={event?.venue?.address_two || "N/A"}
            />
            <DetailField label="City" value={event?.venue?.city || "N/A"} />
            <DetailField
              label="Expected Audience"
              value={event?.expected_audience_size || ""}
            />
            <DetailField
              label="Streaming Platform"
              value={event?.streaming_platform || "N/A"}
            />
          </>
        );

      case "Other":
        return (
          <>
            <DetailField label="Title" value={bookingData?.title || "N/A"} />
            <DetailField label="Host Name" value={event?.hosts || "N/A"} />
            <DetailField
              label="Host Surname"
              value={bookingData?.host_surname || "N/A"}
            />
            <DetailField label="Guest Name" value={event?.guests || "N/A"} />
            <DetailField
              label="Guest Surname"
              value={bookingData?.guest_surname || "N/A"}
            />
            <DetailField
              label="Date of Service"
              value={formatDate(service?.date_of_service) || "N/A"}
            />
            <DetailField
              label="Start Time"
              value={event?.start_time || "N/A"}
            />
            <DetailField label="End Time" value={event?.end_time || "N/A"} />
            <DetailField
              label="Venue Name"
              value={event?.venue.name || "N/A"}
            />
            <DetailField
              label="Service Address"
              value={event?.venue.address_one || "N/A"}
            />
            <DetailField label="City" value={event?.venue.city || "N/A"} />
          </>
        );

      default:
        return (
          <Grid size={{ xs: 12 }}>
            <Typography>
              No event details available for this service type.
            </Typography>
          </Grid>
        );
    }
  };

  return (
    <>
      {renderFields()}

      {/* Venue Dialog */}
      <VenueDetailsDialog
        open={isVenueDialogOpen}
        venue={hoverVenue}
        onClose={handleVenueDialogClose}
      />
    </>
  );
};

export default EventFields;
